import {
  useNotificationService,
  useTimeAgo,
  useTranslation,
} from '@starsoft/common/hooks';
import styles from './styles.module.scss';
import CloseButton from '@starsoft/common/components/Modal/Header/CloseButton';
import { TransactionsCommonMappingModalContentDetailsHeaderProps } from './props';
import { useTransactionById } from '@starsoft/common/hooks/transactions/useTransactionById';
import { FormGroup } from '@starsoft/common/components/FormGroup';
import { Input } from '@starsoft/common/components/Input';
import TransactionResultDetails from '@starsoft/common/components/Transactions/Result/Details';
import { TransactionsCommonMappingModalContentDetailsHeaderSkeleton } from './Skeleton';

/**
 * TransactionsCommonMappingModalContentDetailsHeader Component
 * This component is responsible for rendering the header section of the transaction mapping modal details.
 * It displays the transaction hash, time information, and a close button.
 *
 * @param {TransactionsCommonMappingModalContentDetailsHeaderProps} props - The properties for the component.
 * @param {Function} props.onClose - Callback function to handle closing the modal.
 *
 * @returns {JSX.Element} The rendered component displaying the header of the transaction details.
 *
 * @example
 * // Usage example:
 * <TransactionsCommonMappingModalContentDetailsHeader onClose={() => function('Modal closed')} />
 */
export function TransactionsCommonMappingModalContentDetailsHeader({
  onClose,
}: TransactionsCommonMappingModalContentDetailsHeaderProps) {
  // Hook to handle translations for the 'common' namespace
  const { t } = useTranslation('common');
  // Hook to fetch transaction data by ID and handle loading state
  const { isLoading, transaction } = useTransactionById();
  // Hook to manage notification services
  const NotificationService = useNotificationService();
  // Hook to calculate time ago from the transaction creation date
  const timeAgo = useTimeAgo({
    date: new Date(transaction?.createdAt ?? new Date()),
  });

  /**
   * handleClopyAddress Function
   * This function copies the transaction hash to the clipboard and shows a success notification.
   */
  function handleClopyAddress() {
    if (typeof navigator == 'undefined') {
      return;
    }

    navigator.clipboard.writeText(transaction?.transaction?.hash);
    NotificationService.success({
      title: 'copy_base_title',
      message: 'copy_base_description',
    });
    return;
  }

  // If the transaction data is still loading, render the skeleton component
  if (isLoading) {
    return <TransactionsCommonMappingModalContentDetailsHeaderSkeleton />;
  }

  // Render the header content with transaction details
  return (
    <div className={styles.container}>
      <div className={styles.container__row}>
        <h1 className={styles.container__title}>
          {t('transaction_visualization_title')}
        </h1>
        <CloseButton onClick={onClose} />
      </div>
      <FormGroup label={t('data-grid:hash_label')}>
        <Input
          value={transaction?.transaction?.hash}
          hasPaddingLeft={false}
          hasPaddingRight
          customIcon={
            <i
              className={`fa-solid fa-paste ${styles.container__icon}`}
              onClick={handleClopyAddress}
            />
          }
        />
      </FormGroup>
      <div
        className={`${styles.container__row} ${styles['container__row--wrapped']}`}
      >
        <div className={styles.container__time__relative}>{timeAgo}</div>
        <div className={styles.container__time__absolute}>
          <i className="fa-solid fa-clock" />
          {Intl.DateTimeFormat('en-US', {
            dateStyle: 'full',
          }).format(new Date(transaction?.createdAt ?? new Date()))}
        </div>
      </div>
      <TransactionResultDetails minimal transaction={transaction} />
    </div>
  );
}
